/*!
 * Game Day Personality Quiz - shared engine for all 32 evergreen city/team
 * football celebration-planning pages.
 *
 * One file, one engine, zero dependencies. Every page embeds the same script
 * and stylesheet; the only per-page difference is the city slug:
 *
 *   <div data-gameday-quiz data-city="kansas-city-chiefs"></div>
 *   <script src="gameday-quiz.js" defer></script>
 *
 * Interaction model and visual language match the Choose Recovery embedded
 * bachelorette planner: one step at a time, progress track, tap answers,
 * browser-saved state, calm result screen with print/download/share.
 *
 * Privacy: no network calls, no cookies, no email gate, no tracking. The only
 * storage is an optional localStorage entry holding the visitor's own result
 * on their own device, so a returning visitor can view or retake.
 *
 * Scoring is transparent and deterministic:
 *   - Every answer awards +2 to one primary personality and +1 to one
 *     secondary personality. The full map is published below in QUESTIONS.
 *   - Each of the five personalities is the primary for exactly four answers,
 *     so no result is structurally favored.
 *   - Ties: (1) higher total score wins; (2) tie on total -> the personality
 *     with more primary (+2) answers wins; (3) still tied -> the personality
 *     whose most recent primary answer came later in the quiz wins.
 *   The same explanation belongs in the page FAQ so the rules stay public.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) { module.exports = api; }
  if (root) { root.GameDayQuiz = api; }
}(typeof self !== 'undefined' ? self : null, function () {
  'use strict';

  var VERSION = '3.0.0';
  var STORAGE_KEY = 'chooseRecoveryGameDayQuizV1';

  var DEFAULTS = {
    collectionUrl: '/collections/game-day-recovery-kit',
    disclosure: 'Independent fan-planning editorial. Not affiliated with, sponsored by or endorsed by any professional football league or club.'
  };

  var PERSONALITY_ORDER = ['host', 'captain', 'loyalist', 'social', 'couch'];

  var PERSONALITIES = {
    host: {
      id: 'host',
      name: 'The Host Who Thought Ahead',
      tagline: 'Every crew has one. In yours, it is you.',
      description: 'You know the address, the timing, the ride plan and the three things everyone else will forget. Game day works because you started it yesterday.',
      checklist: [
        'Confirm the headcount and the arrival windows before game day morning.',
        'Send the guest note: the plan for {anchor}, the rendezvous point and the start time.',
        'Stage the host station the night before: serving tools, water, the cleanup kit and the tomorrow card.',
        'Build the day around the local reality: {reality}.',
        'Assign the ride-out and the cleanup hands before kickoff, not after.',
        'Set the weather or transit fallback before anyone asks for it.',
        'Pack your own departure kit, not just everyone else\'s.',
        'Do the two-minute walkthrough before the first guest lands.',
      ],
      localTemplate: 'Hosting note for {city}: {reality}. Your checklist already assumes it.',
      bridge: 'The +reCover(y) leather case sits on your host station beside the checklist, staged the night before as one prepared piece of the plan you already made. Organization, not promises.',
      showCollection: true
    },
    captain: {
      id: 'captain',
      name: 'The Tailgate Captain',
      tagline: 'Every crew has one. In yours, it is you.',
      description: 'You brought the grill, the speaker, and a spreadsheet nobody asked for.',
      checklist: [
        'Confirm the lot rules and opening times at {anchor} before promising anyone a grill.',
        'Pack fuel, tools and a weather backup first, then the fun stuff.',
        'Set the arrival order: who claims the spot, who brings what.',
        'Run cooler math: drinks, ice, and water that is not an afterthought.',
        'Plan around the local reality: {reality}.',
        'Tape the run sheet inside the cooler lid: load-in, kickoff, load-out.',
        'Pack the cleanup kit with the setup kit. Future you says thanks.',
        'Stage tomorrow\'s departure bag before you sleep.',
      ],
      localTemplate: 'Your ground in {city}: {anchor}.',
      bridge: 'The +reCover(y) leather case rides in your setup crate next to the speaker cables and the spreadsheet, one prepared piece of the station. Prepared, not promised.',
      showCollection: true
    },
    loyalist: {
      id: 'loyalist',
      name: 'The Fourth-Quarter Loyalist',
      tagline: 'Every crew has one. In yours, it is you.',
      description: 'You\'re staying until the clock hits zero. Yes, even down 28.',
      checklist: [
        'Plan the full day, not just kickoff: arrival, the long middle, the ride out.',
        'Know the day at {anchor} and where the crew regroups if it splits.',
        'Bring layers for the fourth quarter, whatever the first quarter felt like.',
        'Budget for the local reality: {reality}.',
        'Keep snacks and water for the final drive of the day.',
        'Eat a real meal before the anthem, not at halftime.',
        'Charge your phone and stash the tickets offline.',
        'Give tomorrow-you a plan before you sleep.',
      ],
      localTemplate: '{city} note: {reality}. You will still be there at the end.',
      bridge: 'If you travel for the team, the +reCover(y) leather case fits inside the departure kit you pack before kickoff. One prepared piece of a long day, not a promise about tomorrow.',
      showCollection: false
    },
    social: {
      id: 'social',
      name: 'The Social MVP',
      tagline: 'Every crew has one. In yours, it is you.',
      description: 'You came for the people. Apparently there\'s also a game.',
      checklist: [
        'Know the gathering spot at {anchor} and the move time so the crew stays together.',
        'Bring one thing everyone can share.',
        'Nominate a photo person and a ride checker.',
        'Learn one new name per quarter.',
        'Keep water in the rotation, not just the fun drinks.',
        'Make sure the host actually eats.',
        'Send the group the after-plan before the fourth quarter.',
        'Check the local note: {reality}.',
      ],
      localTemplate: '{city} note: the crew gathers around {anchor}.',
      bridge: null,
      showCollection: false
    },
    couch: {
      id: 'couch',
      name: 'The Couch Commissioner',
      tagline: 'Every crew has one. In yours, it is you.',
      description: 'Your living room. Your rules. Your very questionable referee commentary.',
      checklist: [
        'Stock the room before kickoff, not at halftime.',
        'Drinks cold, water out, remote located.',
        'Send the house note: start time, parking, house rules.',
        'Plan the fourth-quarter food so nobody misses the ending.',
        'Claim the commentary chair. It still has the better view than {anchor}.',
        'Keep one shelf of tomorrow-morning supplies behind the game-day stock.',
        'Clear a landing spot for tomorrow morning.',
        'If the crew heads out instead, check the local note: {reality}.',
      ],
      localTemplate: '{city} note: if the crew heads to {anchor}, your couch still has the better view.',
      bridge: null,
      showCollection: false
    }
  };

  var QUESTIONS = [
    {
      id: 'where', nav: 'Where', text: 'Where are you watching?',
      answers: [
        { label: 'Stadium', primary: 'loyalist', secondary: 'captain' },
        { label: 'Tailgate', primary: 'captain', secondary: 'host' },
        { label: 'Sports bar', primary: 'social', secondary: 'loyalist' },
        { label: 'Someone\'s couch', primary: 'couch', secondary: 'social' },
        { label: 'My place. I\'m hosting.', primary: 'host', secondary: 'captain' }
      ]
    },
    {
      id: 'role', nav: 'Your role', text: 'What\'s your role?',
      answers: [
        { label: 'Grill captain', primary: 'captain', secondary: 'host' },
        { label: 'Hype person', primary: 'social', secondary: 'loyalist' },
        { label: 'Stats expert', primary: 'couch', secondary: 'loyalist' },
        { label: 'Here for the snacks', primary: 'social', secondary: 'couch' },
        { label: 'The one with the plan', primary: 'host', secondary: 'captain' }
      ]
    },
    {
      id: 'loss', nav: 'The loss', text: 'Your team loses. You...',
      answers: [
        { label: 'Demand a coaching change', primary: 'loyalist', secondary: 'couch' },
        { label: 'Find the food', primary: 'captain', secondary: 'social' },
        { label: 'Keep the party going', primary: 'loyalist', secondary: 'social' },
        { label: 'Saw it coming', primary: 'couch', secondary: 'loyalist' },
        { label: 'Start the cleanup and the rides home', primary: 'host', secondary: 'captain' }
      ]
    },
    {
      id: 'tomorrow', nav: 'Tomorrow', text: 'What\'s happening tomorrow?',
      answers: [
        { label: 'Brunch', primary: 'social', secondary: 'couch' },
        { label: 'Travel', primary: 'loyalist', secondary: 'host' },
        { label: 'Family time', primary: 'captain', secondary: 'social' },
        { label: 'Absolutely nothing', primary: 'couch', secondary: 'social' },
        { label: 'Whatever\'s on the calendar. I put it there.', primary: 'host', secondary: 'captain' }
      ]
    }
  ];

  var CITIES = {
    'kansas-city-chiefs': { city: 'Kansas City', team: 'Chiefs', anchor: 'the Ford Tailgate District outside the Hy-Vee Gate at Arrowhead', reality: 'the parking lot is the pregame venue, so arrival windows and the ride-out plan are the job' },
    'philadelphia-eagles': { city: 'Philadelphia', team: 'Eagles', anchor: 'the South Philadelphia Sports Complex lots, from Lot K to the cook-led crews of Lot M', reality: 'three teams share one complex, so check the whole calendar, not just the football game' },
    'dallas-cowboys': { city: 'Dallas', team: 'Cowboys', anchor: 'Lots 4 through 7 outside AT&T Stadium, home of the Lot 4 Gate 6 Tailgaters', reality: 'guests arrive from two metro directions, so the arrival message names the lot, not just the stadium' },
    'buffalo-bills': { city: 'Buffalo', team: 'Bills', anchor: 'Hammer\'s Lot in Orchard Park', reality: 'lake-effect cold puts layers, dry storage and a warm close on the list' },
    'green-bay-packers': { city: 'Green Bay', team: 'Packers', anchor: 'the Titletown district and the neighborhood lots around Lambeau Field', reality: 'lodging and arrival are the scarce resources, booked months out' },
    'detroit-lions': { city: 'Detroit', team: 'Lions', anchor: 'Eastern Market, the tailgating heart of the city', reality: 'pregame and stadium are different places, so the plan needs a move time and a regroup point' },
    'new-orleans-saints': { city: 'New Orleans', team: 'Saints', anchor: 'Champions Square beside the Superdome', reality: 'the official pregame is a ticketed venue, so entry timing belongs in the run of show' },
    'pittsburgh-steelers': { city: 'Pittsburgh', team: 'Steelers', anchor: 'the North Shore lots around Acrisure Stadium', reality: 'the parking operator sets the rules, so check the policy before promising a grill' },
    'miami-dolphins': { city: 'Miami', team: 'Dolphins', anchor: 'the Hard Rock Stadium lots', reality: 'everything is pre-purchased and heat-shaped: shade, water and an indoor fallback' },
    'new-england-patriots': { city: 'New England', team: 'Patriots', anchor: 'Patriot Place around Gillette Stadium', reality: 'guests drive in from six states, so the arrival map and departure windows are the real plan' },
    'new-york-jets': { city: 'New York', team: 'Jets', anchor: 'the MetLife Stadium permit lots', reality: 'the real plan is transit: the rail connection, the lot letter and a rendezvous point that survives late arrivals' },
    'baltimore-ravens': { city: 'Baltimore', team: 'Ravens', anchor: 'downtown parking within a walk of M&T Bank Stadium', reality: 'most guests will not hold a stadium-lot permit, so the walk is part of the gathering' },
    'cincinnati-bengals': { city: 'Cincinnati', team: 'Bengals', anchor: 'Pregame at The Banks on Freedom Way', reality: 'the official pregame has a start time, so newcomers anchor there before the lot or the gate' },
    'cleveland-browns': { city: 'Cleveland', team: 'Browns', anchor: 'the Muni Lot on the lakefront', reality: 'a cold, first-come lakefront lot makes arrival order and warm gear survival tools' },
    'houston-texans': { city: 'Houston', team: 'Texans', anchor: 'the NRG Stadium lots', reality: 'access is ticketed even for tailgating, so confirm every guest\'s credential before load-in' },
    'indianapolis-colts': { city: 'Indianapolis', team: 'Colts', anchor: 'Touchdown Town and the South Lot around Lucas Oil Stadium', reality: 'the roof takes weather off the list, so parking, the meeting point and the postgame regroup stay with the host' },
    'jacksonville-jaguars': { city: 'Jacksonville', team: 'Jaguars', anchor: 'the East Lots by EverBank Stadium', reality: 'heat, water and shade define the brief, and the lot pass goes in the guest note' },
    'tennessee-titans': { city: 'Tennessee', team: 'Titans', anchor: 'the riverfront pregame around Nissan Stadium', reality: 'the stadium sits across the river, so the run sheet covers the crossing and the ride out' },
    'denver-broncos': { city: 'Denver', team: 'Broncos', anchor: 'Lot C at Empower Field at Mile High', reality: 'sun, altitude and a big temperature swing mean the layers plan and the transport plan are built together' },
    'las-vegas-raiders': { city: 'Las Vegas', team: 'Raiders', anchor: 'the designated Allegiant Stadium lots', reality: 'most guests are visitors, so the itinerary runs hotel timing, the walk over the bridge and the postgame ride' },
    'los-angeles-chargers': { city: 'Los Angeles', team: 'Chargers', anchor: 'the Hyundai Pink Zone at SoFi Stadium', reality: 'tailgating fits in one zone, so the neighborhood rendezvous matters more than the lot' },
    'new-york-giants': { city: 'New York', team: 'Giants', anchor: 'Lot L1 at MetLife Stadium, home of the Big Blue crews', reality: 'three generations travel in one plan, so the family bag and the transit leg are the real work' },
    'washington-commanders': { city: 'Washington', team: 'Commanders', anchor: 'the fan-run lots at Northwest Stadium', reality: 'the stadium sits in Landover, so the plan names the Metro connection or the lot letter' },
    'chicago-bears': { city: 'Chicago', team: 'Bears', anchor: 'the Waldron Deck and the 31st Street Lot B by Soldier Field', reality: 'a tight footprint and real winter put the warm base and the ride-home station on the list' },
    'minnesota-vikings': { city: 'Minnesota', team: 'Vikings', anchor: 'the official tailgate lots near U.S. Bank Stadium', reality: 'scarce tailgating shifts the ritual to winter transport and the indoor arrival' },
    'atlanta-falcons': { city: 'Atlanta', team: 'Falcons', anchor: 'The Home Depot Backyard', reality: 'in a transplant city, the official greenspace is the shared ritual ground' },
    'carolina-panthers': { city: 'Carolina', team: 'Panthers', anchor: 'the uptown Charlotte lots within a walk of Bank of America Stadium', reality: 'one fan base spans two states, so pick the lot that splits the difference' },
    'tampa-bay-buccaneers': { city: 'Tampa Bay', team: 'Buccaneers', anchor: 'the public lots at Raymond James Stadium', reality: 'Florida weather decides the day, so the contingency card covers heat, storms and the move indoors' },
    'arizona-cardinals': { city: 'Arizona', team: 'Cardinals', anchor: 'The Great Lawn and Sportsman\'s Park at State Farm Stadium', reality: 'the gathering happens in desert heat even when the game is indoors' },
    'los-angeles-rams': { city: 'Los Angeles', team: 'Rams', anchor: 'the Pink Zone at SoFi Stadium', reality: 'drives are measured in hours, so the ritual bag and the route plan matter more than the lot' },
    'san-francisco-49ers': { city: 'San Francisco', team: '49ers', anchor: 'Empire Row at Green Lot 1 by Levi\'s Stadium', reality: 'the stadium is in Santa Clara, so the multigeneration drive and the meeting pole are the plan' },
    'seattle-seahawks': { city: 'Seattle', team: 'Seahawks', anchor: 'Pioneer Square and the SoDo lots near Lumen Field', reality: 'there is no stadium-lot tailgating, so the ritual is built downtown before the walk south' }
  };

  // ---- Pure scoring (also unit-tested in Node) ----

  function emptyTally() {
    var t = {};
    PERSONALITY_ORDER.forEach(function (id) {
      t[id] = { score: 0, primaries: 0, lastPrimaryIndex: -1 };
    });
    return t;
  }

  // answers: array of answer indexes, one per question.
  // Returns { winner, tally, tieBroken } - deterministic, rules in header.
  function computeResult(answers) {
    if (!Array.isArray(answers) || answers.length !== QUESTIONS.length) {
      throw new Error('Expected ' + QUESTIONS.length + ' answers, got ' + (answers && answers.length));
    }
    var tally = emptyTally();
    answers.forEach(function (answerIndex, questionIndex) {
      var answer = QUESTIONS[questionIndex].answers[answerIndex];
      if (!answer) { throw new Error('Bad answer index ' + answerIndex + ' for question ' + questionIndex); }
      tally[answer.primary].score += 2;
      tally[answer.primary].primaries += 1;
      tally[answer.primary].lastPrimaryIndex = questionIndex;
      tally[answer.secondary].score += 1;
    });
    var winner = PERSONALITY_ORDER[0];
    var tieBroken = false;
    PERSONALITY_ORDER.forEach(function (id) {
      var best = tally[winner];
      var cur = tally[id];
      if (cur.score > best.score) { winner = id; return; }
      if (cur.score === best.score && id !== winner) {
        if (cur.primaries > best.primaries) { winner = id; tieBroken = true; return; }
        if (cur.primaries === best.primaries && cur.lastPrimaryIndex > best.lastPrimaryIndex) {
          winner = id; tieBroken = true;
        }
      }
    });
    return { winner: winner, tally: tally, tieBroken: tieBroken };
  }

  function interpolate(template, cityData) {
    return template
      .replace(/\{city\}/g, cityData.city)
      .replace(/\{team\}/g, cityData.team)
      .replace(/\{anchor\}/g, cityData.anchor)
      .replace(/\{reality\}/g, cityData.reality);
  }

  function shareText(personality, cityData, url) {
    return 'I got ' + personality.name + ' on the ' + cityData.city +
      ' game day personality quiz. Every crew has one. Which one are you? ' + url;
  }

  function summaryText(personality, cityData, checks) {
    var lines = [];
    lines.push(personality.name.toUpperCase());
    lines.push(personality.description);
    lines.push('');
    lines.push('YOUR GAME-DAY CHECKLIST');
    personality.checklist.forEach(function (item, i) {
      lines.push((checks && checks[i] ? '[x] ' : '[ ] ') + interpolate(item, cityData));
    });
    lines.push('');
    lines.push(cityData.city + ' note');
    lines.push(interpolate(personality.localTemplate, cityData));
    if (personality.bridge) {
      lines.push('');
      lines.push('A ready-made option, if it fits the plan');
      lines.push(personality.bridge);
    }
    lines.push('');
    lines.push(DEFAULTS.disclosure);
    return lines.join('\n');
  }

  // ---- DOM engine (browser only) ----

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) { node.className = className; }
    if (text != null) { node.textContent = text; }
    return node;
  }

  function mount(container, opts) {
    opts = opts || {};
    if (container.getAttribute('data-crq-mounted') === 'true') { return null; }
    container.setAttribute('data-crq-mounted', 'true');
    var citySlug = opts.city || container.getAttribute('data-city') || 'kansas-city-chiefs';
    var cityData = CITIES[citySlug];
    if (!cityData) { throw new Error('Unknown city slug: ' + citySlug); }
    var config = {
      collectionUrl: opts.collectionUrl || DEFAULTS.collectionUrl,
      disclosure: opts.disclosure || DEFAULTS.disclosure
    };

    var STEP_COUNT = QUESTIONS.length + 2; // start + questions + result
    var RESULT_STEP = STEP_COUNT - 1;
    var state = { step: 0, answers: [], result: null, checks: [] };

    // ---- persistence ----
    function store() {
      try {
        window.localStorage.setItem(STORAGE_KEY + ':' + citySlug, JSON.stringify({
          answers: state.answers, result: state.result, checks: state.checks, ts: Date.now()
        }));
      } catch (e) { /* private mode: state simply is not remembered */ }
    }
    function recall() {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY + ':' + citySlug);
        return raw ? JSON.parse(raw) : null;
      } catch (e) { return null; }
    }
    function forget() {
      try { window.localStorage.removeItem(STORAGE_KEY + ':' + citySlug); } catch (e) { /* ignore */ }
    }

    // ---- shell (planner structure) ----
    container.classList.add('crq');
    container.innerHTML =
      '<div class="crq-mobile">' +
        '<div class="crq-kicker">Game day personalities</div>' +
        '<div class="crq-track"><div class="crq-fill"></div></div>' +
        '<div class="crq-progress"></div>' +
      '</div>' +
      '<div class="crq-shell">' +
        '<aside class="crq-side">' +
          '<div class="crq-kicker">Game day personalities</div>' +
          '<div class="crq-brand">Every crew has one</div>' +
          '<div class="crq-track"><div class="crq-fill"></div></div>' +
          '<div class="crq-progress"></div>' +
          '<ol class="crq-nav"></ol>' +
          '<p class="crq-privacy">Private by default. Your answers stay in this browser unless you share, download or print them.</p>' +
        '</aside>' +
        '<main class="crq-main">' +
          '<div class="crq-steps"></div>' +
          '<div class="crq-save" aria-live="polite"></div>' +
          '<pre class="crq-print"></pre>' +
          '<noscript>Turn on JavaScript to use the interactive quiz.</noscript>' +
        '</main>' +
      '</div>';

    var navList = container.querySelector('.crq-nav');
    var stepsWrap = container.querySelector('.crq-steps');
    var saveNote = container.querySelector('.crq-save');
    var printPre = container.querySelector('.crq-print');
    var fills = container.querySelectorAll('.crq-fill');
    var progressLabels = container.querySelectorAll('.crq-progress');

    var NAV = [{ label: 'Start' }]
      .concat(QUESTIONS.map(function (q, i) { return { label: (i + 1) + '. ' + q.nav }; }))
      .concat([{ label: 'Your result' }]);

    var navButtons = NAV.map(function (item, i) {
      var li = el('li');
      var btn = el('button');
      btn.type = 'button';
      btn.appendChild(el('span', 'crq-dot', i === 0 ? '' : (i === RESULT_STEP ? '*' : String(i))));
      btn.appendChild(el('span', null, item.label));
      btn.addEventListener('click', function () { goTo(i); });
      li.appendChild(btn);
      navList.appendChild(li);
      return btn;
    });

    function updateChrome() {
      var pct = Math.max(8, Math.round(((state.step + 1) / STEP_COUNT) * 100));
      Array.prototype.forEach.call(fills, function (f) { f.style.width = pct + '%'; });
      var label = state.step === 0 ? 'Not started'
        : state.step === RESULT_STEP ? 'Result ready'
        : 'Question ' + state.step + ' of ' + QUESTIONS.length;
      Array.prototype.forEach.call(progressLabels, function (p) { p.textContent = label; });
      navButtons.forEach(function (btn, i) {
        var done = (i > 0 && i <= QUESTIONS.length && state.answers[i - 1] != null) ||
          (i === RESULT_STEP && !!state.result);
        btn.setAttribute('data-done', done ? 'true' : 'false');
        if (i === state.step) { btn.setAttribute('aria-current', 'step'); }
        else { btn.removeAttribute('aria-current'); }
        var locked = (i === RESULT_STEP && !state.result);
        btn.disabled = locked;
      });
    }

    function goTo(step) {
      if (step === RESULT_STEP && !state.result) { return; }
      state.step = step;
      render();
    }

    function focusMainHeading() {
      var h = stepsWrap.querySelector('h2');
      if (h) { h.setAttribute('tabindex', '-1'); h.focus(); }
    }

    function render() {
      stepsWrap.innerHTML = '';
      saveNote.textContent = '';
      if (state.step === 0) { renderStart(); }
      else if (state.step === RESULT_STEP) { renderResult(); }
      else { renderQuestion(state.step - 1); }
      updateChrome();
      focusMainHeading();
    }

    function renderStart() {
      var step = el('section', 'crq-step active');
      var saved = recall();
      if (saved && saved.result && PERSONALITIES[saved.result]) {
        var banner = el('div', 'crq-saved');
        banner.appendChild(el('span', null, 'Welcome back. Last time you were ' + PERSONALITIES[saved.result].name + '.'));
        var viewBtn = el('button', 'crq-btn ghost', 'View my result');
        viewBtn.type = 'button';
        viewBtn.addEventListener('click', function () {
          state.answers = saved.answers || [];
          state.result = saved.result;
          state.checks = saved.checks || [];
          goTo(RESULT_STEP);
        });
        banner.appendChild(viewBtn);
        step.appendChild(banner);
      }
      step.appendChild(el('p', 'crq-eye', 'Four quick questions'));
      step.appendChild(el('h2', null, 'What\'s Your Game Day Personality?'));
      step.appendChild(el('p', 'crq-lede',
        'Every crew has one. Which one are you? One tap per answer, no email, nothing to install. ' +
        'Your result is a checklist built for how you actually do game day in ' + cityData.city + '.'));
      var startBtn = el('button', 'crq-btn', saved && saved.result ? 'Retake the quiz' : 'Start the quiz');
      startBtn.type = 'button';
      startBtn.addEventListener('click', function () {
        state.answers = [];
        state.result = null;
        state.checks = [];
        goTo(1);
      });
      step.appendChild(startBtn);
      step.appendChild(el('p', 'crq-disclosure',
        'How scoring works: every answer counts toward a personality. The strongest total wins; ties break toward your most recent strong answer. ' + config.disclosure));
      stepsWrap.appendChild(step);
    }

    function renderQuestion(qi) {
      var q = QUESTIONS[qi];
      var step = el('section', 'crq-step active');
      step.appendChild(el('p', 'crq-eye', 'Question ' + (qi + 1) + ' of ' + QUESTIONS.length));
      step.appendChild(el('h2', null, q.text));
      var list = el('ul', 'crq-answers');
      q.answers.forEach(function (answer, i) {
        var item = el('li');
        var btn = el('button', 'crq-answer');
        btn.type = 'button';
        btn.appendChild(el('span', null, answer.label));
        btn.appendChild(el('span', 'crq-chev', '›'));
        if (state.answers[qi] === i) { btn.style.borderColor = 'var(--green)'; btn.style.background = 'var(--soft)'; }
        btn.addEventListener('click', function () {
          state.answers[qi] = i;
          if (qi + 1 < QUESTIONS.length) {
            goTo(qi + 2);
          } else {
            var result = computeResult(state.answers);
            state.result = result.winner;
            state.checks = PERSONALITIES[result.winner].checklist.map(function () { return false; });
            store();
            saveNote.textContent = 'Saved on this device.';
            goTo(RESULT_STEP);
          }
        });
        item.appendChild(btn);
        list.appendChild(item);
      });
      step.appendChild(list);
      var back = el('button', 'crq-back', '‹ Back');
      back.type = 'button';
      back.addEventListener('click', function () { goTo(state.step - 1); });
      step.appendChild(back);
      stepsWrap.appendChild(step);
    }

    function renderResult() {
      var p = PERSONALITIES[state.result];
      var step = el('section', 'crq-step active');

      var finish = el('div', 'crq-finish');
      finish.appendChild(el('p', 'crq-eye', 'Your game day personality'));
      finish.appendChild(el('h2', null, p.name));
      finish.appendChild(el('p', null, p.description));
      step.appendChild(finish);

      var checklistCard = el('div', 'crq-card');
      checklistCard.appendChild(el('h3', null, 'Your ' + cityData.city + ' game-day checklist'));
      checklistCard.appendChild(el('p', null, 'Tap to check things off. It saves here, so you can come back to it.'));
      var list = el('div', 'crq-checklist');
      p.checklist.forEach(function (item, i) {
        var label = el('label', 'crq-check');
        var box = document.createElement('input');
        box.type = 'checkbox';
        box.checked = !!state.checks[i];
        box.addEventListener('change', function () {
          state.checks[i] = box.checked;
          store();
          saveNote.textContent = 'Saved on this device.';
        });
        label.appendChild(box);
        label.appendChild(el('span', null, interpolate(item, cityData)));
        list.appendChild(label);
      });
      checklistCard.appendChild(list);
      step.appendChild(checklistCard);

      step.appendChild(el('p', 'crq-local', interpolate(p.localTemplate, cityData)));

      if (p.bridge) {
        var bridge = el('aside', 'crq-bridge');
        bridge.appendChild(el('h3', null, 'A ready-made option, if it fits the plan'));
        bridge.appendChild(el('p', null, p.bridge));
        var link = el('a', 'crq-btn secondary', 'Explore +reCover(y)');
        link.href = config.collectionUrl;
        bridge.appendChild(link);
        step.appendChild(bridge);
      }

      var actions = el('div', 'crq-actions');
      var shareBtn = el('button', 'crq-btn secondary', 'Share your result');
      shareBtn.type = 'button';
      shareBtn.addEventListener('click', function () {
        var url = (typeof location !== 'undefined' && location.href) ? location.href.split('?')[0] : '';
        var text = shareText(p, cityData, url);
        if (typeof navigator !== 'undefined' && navigator.share) {
          navigator.share({ title: p.name, text: text }).catch(function () { /* visitor dismissed the sheet */ });
        } else if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(function () {
            saveNote.textContent = 'Copied. Paste it anywhere.';
          }, function () { saveNote.textContent = text; });
        } else {
          saveNote.textContent = text;
        }
      });
      actions.appendChild(shareBtn);

      var downloadBtn = el('button', 'crq-btn ghost', 'Download');
      downloadBtn.type = 'button';
      downloadBtn.addEventListener('click', function () {
        var blob = new Blob([summaryText(p, cityData, state.checks)], { type: 'text/plain' });
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'game-day-personality-' + p.id + '.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(function () { URL.revokeObjectURL(a.href); }, 1000);
      });
      actions.appendChild(downloadBtn);

      var printBtn = el('button', 'crq-btn ghost', 'Print');
      printBtn.type = 'button';
      printBtn.addEventListener('click', function () {
        printPre.textContent = summaryText(p, cityData, state.checks);
        window.print();
      });
      actions.appendChild(printBtn);

      var retakeBtn = el('button', 'crq-btn ghost', 'Retake');
      retakeBtn.type = 'button';
      retakeBtn.addEventListener('click', function () {
        forget();
        state = { step: 0, answers: [], result: null, checks: [] };
        goTo(0);
      });
      actions.appendChild(retakeBtn);
      step.appendChild(actions);

      step.appendChild(el('p', 'crq-disclosure', config.disclosure));
      stepsWrap.appendChild(step);
    }

    render();
    return {
      goTo: goTo,
      answer: function (qi, ai) { state.answers[qi] = ai; },
      finish: function () {
        state.result = computeResult(state.answers).winner;
        state.checks = PERSONALITIES[state.result].checklist.map(function () { return false; });
        store();
        goTo(RESULT_STEP);
      },
      getState: function () { return JSON.parse(JSON.stringify(state)); },
      city: citySlug
    };
  }

  function autoMount() {
    if (typeof document === 'undefined') { return; }
    var nodes = document.querySelectorAll('[data-gameday-quiz]');
    Array.prototype.forEach.call(nodes, function (node) { mount(node, {}); });
  }
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', autoMount);
    } else {
      autoMount();
    }
  }

  return {
    VERSION: VERSION,
    STORAGE_KEY: STORAGE_KEY,
    DEFAULTS: DEFAULTS,
    PERSONALITY_ORDER: PERSONALITY_ORDER,
    PERSONALITIES: PERSONALITIES,
    QUESTIONS: QUESTIONS,
    CITIES: CITIES,
    computeResult: computeResult,
    interpolate: interpolate,
    shareText: shareText,
    summaryText: summaryText,
    mount: mount
  };
}));
